import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Avatar,
  Grid,
  FormControlLabel,
  Checkbox,
  Switch,
  Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Refresh,
  CloudUpload,
  LocalShipping,
  Visibility,
  Close,
  Edit,
  CallSplit,
  MergeType,
  AutoFixHigh,
  Gavel,
  Send,
  AttachFile,
  CheckCircle,
  Calculate, // Import Calculate icon
  Groups,
  DeleteForever,
  Sms,
  AccountBalance,
} from '@mui/icons-material';
import { fetchPackages, uploadPackageInfo, deletePackagePhoto, shipPackage, markPackagePickupAvailable, resendPackagePickupMessage, bulkMarkPackagesPickedUpSilently, fetchPackageById, updatePackage, updatePackageLogisticsFees, deletePackage, splitPackage, splitPackageByGoodsType, mergePackages, replyDispute, resolveDispute, distributeShippingCosts, fetchTransferPaymentPreview, payPackageWithTransfer } from '../../services/package';
import { uploadFile } from '../../services/api';
import { fetchLogistics } from '../../services/logistics';
import { getPoolDetails } from '../../services/groupShippingService';
import { getPackagingMaterials } from '../../services/packagingMaterial';
import CustomPagination from '../../components/common/CustomPagination';
import api from '../../services/api';
import { hasEndpointAccess } from '../../utils/rbac';

const addSerialNumbers = (data, page, pageSize) =>
  data.map((row, idx) => ({
    ...row,
    sn: page * pageSize + idx + 1,
  }));

const SPLITTABLE_ORDER_STATUSES = [3, 4];
const SILENT_PICKUP_ELIGIBLE_STATUSES = ['shipped', 'in_nigeria'];
const MERGE_ELIGIBLE_STATUSES = ['pending', 'ready_to_package', 'ready_for_material_selection', 'material_selected', 'packaging', 'packaged'];
const SELECTABLE_PACKAGE_STATUSES = [...new Set([...SILENT_PICKUP_ELIGIBLE_STATUSES, ...MERGE_ELIGIBLE_STATUSES])];

const PackageImagePreview = ({ file, alt, sx }) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <Box
      component="img"
      src={previewUrl}
      alt={alt}
      sx={sx}
    />
  );
};

const EditableFeeCell = ({ row, field, prefix, saving, disabled, onCommit }) => {
  const rawValue = row[field];
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(rawValue === null || rawValue === undefined ? '' : String(rawValue));

  useEffect(() => {
    setValue(rawValue === null || rawValue === undefined ? '' : String(rawValue));
  }, [rawValue, row.id]);

  const displayValue = rawValue === null || rawValue === undefined || rawValue === ''
    ? '—'
    : `${prefix}${Number.parseFloat(rawValue).toFixed(2)}`;

  const handleCommit = () => {
    setEditing(false);
    onCommit(row, field, value);
  };

  if (editing && !disabled) {
    return (
      <TextField
        autoFocus
        size="small"
        value={value}
        disabled={saving}
        type="number"
        inputProps={{ min: 0, step: '0.01' }}
        onChange={(event) => setValue(event.target.value)}
        onBlur={handleCommit}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }
          if (event.key === 'Escape') {
            setValue(rawValue === null || rawValue === undefined ? '' : String(rawValue));
            setEditing(false);
          }
        }}
        sx={{
          width: '100%',
          '& .MuiInputBase-input': {
            py: 0.5,
            fontSize: '0.875rem',
          },
        }}
      />
    );
  }

  return (
    <Tooltip title={disabled ? 'Paid package fees cannot be edited' : 'Click to edit fee'}>
      <Box
        onClick={(event) => {
          event.stopPropagation();
          if (!disabled && !saving) setEditing(true);
        }}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: disabled ? 'text.disabled' : 'text.primary',
        }}
      >
        {saving ? <CircularProgress size={14} /> : <Typography variant="body2">{displayValue}</Typography>}
        {!disabled && !saving && <Edit sx={{ fontSize: 14, color: 'text.secondary' }} />}
      </Box>
    </Tooltip>
  );
};

const PackagesPage = () => {
  const { t } = useTranslation(['packages', 'common']);
  const user = useSelector((state) => state.auth.user);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState('10');
  const [totalPackages, setTotalPackages] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inPoolFilter, setInPoolFilter] = useState(false);

  const canDeletePackages = !!user && hasEndpointAccess(user, 'package-detail', 'DELETE');
  
  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [transferPaymentDialogOpen, setTransferPaymentDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  // Split/Merge states
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [splitLoading, setSplitLoading] = useState(false);
  const [selectedPackageIds, setSelectedPackageIds] = useState([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  
  // Upload form states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [deletingPackagePhoto, setDeletingPackagePhoto] = useState({});
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Edit form states
  const [editWeight, setEditWeight] = useState('');
  const [editCbm, setEditCbm] = useState('');
  const [editDimension, setEditDimension] = useState('');
  const [editCarton, setEditCarton] = useState('');
  const [editShippingMethod, setEditShippingMethod] = useState('');
  const [editShippingCost, setEditShippingCost] = useState('');
  const [editPackingFee, setEditPackingFee] = useState('');
  const [editPickupOption, setEditPickupOption] = useState('platform_pickup');
  const [editEnforcePaymentBeforeShipping, setEditEnforcePaymentBeforeShipping] = useState(true);
  const [editLoading, setEditLoading] = useState(false);
  const [editLogisticsId, setEditLogisticsId] = useState('');
  const [availableLogistics, setAvailableLogistics] = useState([]);
  const [logisticsLoading, setLogisticsLoading] = useState(false);
  const [enforcePaymentToggleLoading, setEnforcePaymentToggleLoading] = useState({});
  const [pickupAvailableLoading, setPickupAvailableLoading] = useState({});
  const [resendPickupMessageLoading, setResendPickupMessageLoading] = useState({});
  const [silentPickupLoading, setSilentPickupLoading] = useState(false);
  
  // Dispute dialog states
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [disputePackage, setDisputePackage] = useState(null);
  const [disputeData, setDisputeData] = useState(null);
  const [disputeReply, setDisputeReply] = useState('');
  const [disputeReplyImages, setDisputeReplyImages] = useState([]);
  const [disputeReplyLoading, setDisputeReplyLoading] = useState(false);
  const [disputeResolveLoading, setDisputeResolveLoading] = useState(false);
  const [disputeResolutionNotes, setDisputeResolutionNotes] = useState('');
  
  // Cost Distribution states
  const [distributeDialogOpen, setDistributeDialogOpen] = useState(false);
  const [distributeLoading, setDistributeLoading] = useState(false);
  const [distributeTotalCost, setDistributeTotalCost] = useState('');
  const [distributeDueDate, setDistributeDueDate] = useState('');
  const [transferPaymentPreview, setTransferPaymentPreview] = useState(null);
  const [transferPaymentLoading, setTransferPaymentLoading] = useState(false);
  const [transferPaymentSubmitting, setTransferPaymentSubmitting] = useState(false);
  const [transferPaymentReceipt, setTransferPaymentReceipt] = useState(null);
  const [transferPaymentNote, setTransferPaymentNote] = useState('');
  const [transferPaymentConfirmed, setTransferPaymentConfirmed] = useState(false);
  const [transferPaymentSavedApproval, setTransferPaymentSavedApproval] = useState(null);

  // Pool details dialog states
  const [poolDetailsDialogOpen, setPoolDetailsDialogOpen] = useState(false);
  const [poolDetails, setPoolDetails] = useState(null);
  const [poolDetailsLoading, setPoolDetailsLoading] = useState(false);

  // Material assessment states
  const [materialAssessmentForm, setMaterialAssessmentForm] = useState({
    estimated_size: '',
    estimated_material_type: '',
    notes: ''
  });
  const [assessingMaterial, setAssessingMaterial] = useState(false);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [actualMaterialForm, setActualMaterialForm] = useState({
    material_id: '',
    notes: ''
  });
  const [settingActualMaterial, setSettingActualMaterial] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [feeSaving, setFeeSaving] = useState({});

  const loadPackages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (inPoolFilter) params.in_pool = 'true';
      
      const response = await fetchPackages(params);
      setPackages(response.results || []);
      setTotalPackages(response.count || 0);
    } catch (error) {
      console.error('Error loading packages:', error);
      setSnackbar({ open: true, message: t('packages:messages.failedToLoad'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, inPoolFilter, t]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    if (!customRowsActive) {
      setCustomRowsInput(pageSize.toString());
    }
  }, [pageSize, customRowsActive]);

  const totalPages = Math.ceil(totalPackages / pageSize) || 1;
  const selectedPackages = packages.filter((pkg) => selectedPackageIds.includes(pkg.id));
  const selectedSilentPickupIds = selectedPackages
    .filter((pkg) => SILENT_PICKUP_ELIGIBLE_STATUSES.includes(pkg.packaging_status))
    .map((pkg) => pkg.id);
  const selectedMergeBlockedPackages = selectedPackages.filter(
    (pkg) => !MERGE_ELIGIBLE_STATUSES.includes(pkg.packaging_status)
  );

  const handleUploadClick = (pkg) => {
    setSelectedPackage(pkg);
    setSelectedFiles([]);
    setUploadDialogOpen(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(pageSize.toString());
      return;
    }
    setCustomRowsActive(false);
    setPageSize(Number(value));
    setPage(0);
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (!Number.isNaN(value) && value > 0) {
      setPageSize(value);
      setPage(0);
    } else {
      setCustomRowsInput(pageSize.toString());
    }
    setCustomRowsActive(false);
  };

  const handleViewDetails = async (pkg) => {
    try {
      const details = await fetchPackageById(pkg.id);
      setSelectedPackage(details);
      setSelectedOrderIds([]);
      setDetailsDialogOpen(true);
      
      // Load available materials if needed
      if (details.packaging_status === 'ready_to_package' || details.packaging_status === 'ready_for_material_selection' || details.packaging_status === 'material_selected' || details.packaging_status === 'packaging') {
        try {
          const materials = await getPackagingMaterials({ is_active: true });
          setAvailableMaterials(Array.isArray(materials) ? materials : materials.results || []);
        } catch (error) {
          console.error('Failed to load materials:', error);
        }
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load package details', severity: 'error' });
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedOrderIds([]);
  };
  
  const handleAssessMaterial = async () => {
    if (!selectedPackage || !materialAssessmentForm.estimated_size || !materialAssessmentForm.estimated_material_type) {
      setSnackbar({ open: true, message: 'Please select estimated size and material type', severity: 'error' });
      return;
    }
    
    try {
      setAssessingMaterial(true);
      const response = await api.post(
        `/admin/packages/${selectedPackage.id}/assess-material/`,
        materialAssessmentForm,
        { requiresAuth: true }
      );
      
      if (response.data.success) {
        setSnackbar({ open: true, message: response.data.message || 'Material assessment saved successfully', severity: 'success' });
        // Reload package details
        const details = await fetchPackageById(selectedPackage.id);
        setSelectedPackage(details);
        setMaterialAssessmentForm({ estimated_size: '', estimated_material_type: '', notes: '' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || error.message || 'Failed to assess material', severity: 'error' });
    } finally {
      setAssessingMaterial(false);
    }
  };
  
  const handleSetActualMaterial = async () => {
    if (!selectedPackage || !actualMaterialForm.material_id) {
      setSnackbar({ open: true, message: 'Please select a material', severity: 'error' });
      return;
    }
    
    try {
      setSettingActualMaterial(true);
      const response = await api.post(
        `/admin/packages/${selectedPackage.id}/set-actual-material/`,
        actualMaterialForm,
        { requiresAuth: true }
      );
      
      if (response.data.success) {
        setSnackbar({ 
          open: true, 
          message: response.data.message || 'Actual material set successfully' + (response.data.invoice_created ? `. Invoice ${response.data.invoice_number} created.` : ''),
          severity: 'success' 
        });
        // Reload package details
        const details = await fetchPackageById(selectedPackage.id);
        setSelectedPackage(details);
        setActualMaterialForm({ material_id: '', notes: '' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || error.message || 'Failed to set actual material', severity: 'error' });
    } finally {
      setSettingActualMaterial(false);
    }
  };

  const acceptedPackageImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxPackageImageSize = 10 * 1024 * 1024;

  const addPackageFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const validFiles = [];
    const rejectedFiles = [];

    files.forEach((file) => {
      if (!acceptedPackageImageTypes.includes(file.type)) {
        rejectedFiles.push(`${file.name} (invalid file type)`);
        return;
      }

      if (file.size > maxPackageImageSize) {
        rejectedFiles.push(`${file.name} (larger than 10MB)`);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }

    if (rejectedFiles.length > 0) {
      setSnackbar({
        open: true,
        message: `Some images were not added: ${rejectedFiles.join(', ')}`,
        severity: 'warning'
      });
    }
  };

  const handleFileSelect = (event) => {
    addPackageFiles(event.target.files);
    event.target.value = '';
  };

  const handlePackageUploadDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handlePackageUploadDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    addPackageFiles(event.dataTransfer.files);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadSubmit = async () => {
    if (selectedFiles.length === 0) {
      setSnackbar({ open: true, message: t('packages:messages.pleaseSelectPhotos'), severity: 'warning' });
      return;
    }

    setUploadLoading(true);
    try {
      const photoUrls = [];
      const failedUploads = [];

      for (const file of selectedFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('folder', 'packages');

          const uploadResponse = await uploadFile(formData);
          const imageUrl = uploadResponse.url || uploadResponse.file_url;
          if (uploadResponse.success && imageUrl) {
            photoUrls.push(imageUrl);
          } else {
            failedUploads.push(file.name);
          }
        } catch (err) {
          console.error('Error uploading file:', err);
          failedUploads.push(file.name);
        }
      }

      if (failedUploads.length > 0) {
        setSnackbar({
          open: true,
          message: `Failed to upload: ${failedUploads.join(', ')}`,
          severity: 'error'
        });
        return;
      }

      const response = await uploadPackageInfo(selectedPackage.id, {
        package_photos: photoUrls
      });

      if (response.success) {
        setSnackbar({ open: true, message: t('packages:messages.packageInfoUploadedSuccess'), severity: 'success' });
        loadPackages();
        handleCloseUploadDialog();
      } else {
        setSnackbar({ open: true, message: response.message || t('packages:messages.uploadFailed'), severity: 'error' });
      }
    } catch (error) {
      console.error('Error uploading package info:', error);
      setSnackbar({ open: true, message: t('packages:messages.failedToUploadPackageInfo'), severity: 'error' });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeletePackagePhoto = async (photoUrl, index) => {
    if (!selectedPackage) return;
    if (!window.confirm('Delete this package proof image?')) return;

    const deleteKey = `${selectedPackage.id}-${index}`;
    setDeletingPackagePhoto(prev => ({ ...prev, [deleteKey]: true }));

    try {
      const response = await deletePackagePhoto(selectedPackage.id, photoUrl);
      if (response.success) {
        setSnackbar({ open: true, message: response.message || 'Package photo deleted', severity: 'success' });
        if (response.package) {
          setSelectedPackage(response.package);
        } else {
          setSelectedPackage(prev => prev ? {
            ...prev,
            package_photos: (prev.package_photos || []).filter((photo) => photo !== photoUrl)
          } : prev);
        }
        loadPackages();
      } else {
        setSnackbar({ open: true, message: response.error || 'Failed to delete package photo', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete package photo',
        severity: 'error'
      });
    } finally {
      setDeletingPackagePhoto(prev => {
        const next = { ...prev };
        delete next[deleteKey];
        return next;
      });
    }
  };

  const handleShip = async (pkg) => {
    if (!window.confirm(t('packages:messages.markAsShippedConfirm', { packageNumber: pkg.package_number }))) return;

    try {
      const response = await shipPackage(pkg.id);
      if (response.success) {
        setSnackbar({ open: true, message: t('packages:messages.packageMarkedAsShipped'), severity: 'success' });
        loadPackages();
      }
    } catch (error) {
      setSnackbar({ open: true, message: t('packages:messages.failedToShipPackage'), severity: 'error' });
    }
  };

  const handleMarkPickupAvailable = async (pkg) => {
    if (!window.confirm(`Confirm that package ${pkg.package_number} is available for pickup? It will be marked as picked up automatically after 7 days unless the customer confirms earlier.`)) return;

    setPickupAvailableLoading((prev) => ({ ...prev, [pkg.id]: true }));
    try {
      const response = await markPackagePickupAvailable(pkg.id);
      setSnackbar({ open: true, message: response.message, severity: 'success' });
      loadPackages();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to mark package available for pickup',
        severity: 'error'
      });
    } finally {
      setPickupAvailableLoading((prev) => ({ ...prev, [pkg.id]: false }));
    }
  };

  const handleResendPickupMessage = async (pkg) => {
    if (!window.confirm(`Resend the pickup-ready message for package ${pkg.package_number} to the customer via notification, in-app message, SMS, and email?`)) return;

    setResendPickupMessageLoading((prev) => ({ ...prev, [pkg.id]: true }));
    try {
      const response = await resendPackagePickupMessage(pkg.id);
      setSnackbar({ open: true, message: response.message, severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to resend pickup-ready message',
        severity: 'error'
      });
    } finally {
      setResendPickupMessageLoading((prev) => ({ ...prev, [pkg.id]: false }));
    }
  };

  const handleBulkMarkPickedUpSilently = async () => {
    if (selectedSilentPickupIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select at least one shipped or in-Nigeria package to mark picked up silently.',
        severity: 'warning'
      });
      return;
    }

    if (!window.confirm(`Silently mark ${selectedSilentPickupIds.length} selected package(s) as picked up? Customers will not receive pickup-ready messages.`)) return;

    setSilentPickupLoading(true);
    try {
      const response = await bulkMarkPackagesPickedUpSilently(selectedSilentPickupIds);
      setSnackbar({
        open: true,
        message: response.message || 'Packages marked picked up silently.',
        severity: 'success'
      });
      setSelectedPackageIds([]);
      loadPackages();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to silently mark packages picked up',
        severity: 'error'
      });
    } finally {
      setSilentPickupLoading(false);
    }
  };

  const handleOpenTransferPaymentDialog = async (pkg) => {
    setSelectedPackage(pkg);
    setTransferPaymentDialogOpen(true);
    setTransferPaymentLoading(true);
    setTransferPaymentPreview(null);
    setTransferPaymentSavedApproval(null);
    setTransferPaymentReceipt(null);
    setTransferPaymentNote('');
    setTransferPaymentConfirmed(false);

    try {
      const response = await fetchTransferPaymentPreview(pkg.id);
      const preview = response?.preview || null;
      const latestApproval = response?.latest_approval || null;
      setTransferPaymentPreview(preview);
      setTransferPaymentSavedApproval(latestApproval);

      if (latestApproval) {
        setTransferPaymentNote(latestApproval.note || '');
        setTransferPaymentConfirmed(true);
      }

      if (!preview?.can_process && !latestApproval) {
        setSnackbar({
          open: true,
          message: preview?.blocking_reasons?.[0] || 'No outstanding package payment was found',
          severity: 'warning',
        });
      }
    } catch (error) {
      setTransferPaymentDialogOpen(false);
      setSelectedPackage(null);
      setSnackbar({
        open: true,
        message: error?.response?.data?.error || 'Failed to load transfer payment preview',
        severity: 'error',
      });
    } finally {
      setTransferPaymentLoading(false);
    }
  };

  const handleCloseTransferPaymentDialog = () => {
    setTransferPaymentDialogOpen(false);
    setTransferPaymentPreview(null);
    setTransferPaymentSavedApproval(null);
    setTransferPaymentReceipt(null);
    setTransferPaymentNote('');
    setTransferPaymentConfirmed(false);
    setSelectedPackage(null);
  };

  const handleTransferPaymentSubmit = async () => {
    if (!selectedPackage || !transferPaymentPreview?.can_process || !transferPaymentReceipt || !transferPaymentConfirmed) {
      return;
    }

    setTransferPaymentSubmitting(true);
    try {
      const response = await payPackageWithTransfer(selectedPackage.id, {
        confirm_received: transferPaymentConfirmed,
        receipt: transferPaymentReceipt,
        note: transferPaymentNote,
      });
      setSnackbar({
        open: true,
        message: response?.message || 'Transfer payment recorded successfully',
        severity: 'success',
      });
      handleCloseTransferPaymentDialog();
      loadPackages();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.error || 'Failed to record transfer payment',
        severity: 'error',
      });
    } finally {
      setTransferPaymentSubmitting(false);
    }
  };

  const handleDeletePackage = async (pkg) => {
    const packageNumber = pkg?.package_number || pkg?.id;
    if (!window.confirm(`Delete package ${packageNumber}? This cannot be undone.`)) return;
    try {
      await deletePackage(pkg.id);
      setSnackbar({ open: true, message: 'Package deleted', severity: 'success' });
      loadPackages();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || 'Failed to delete package',
        severity: 'error'
      });
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedPackage(null);
    setSelectedFiles([]);
    setDragActive(false);
  };

  const handleEnforcePaymentToggle = async (pkg, checked) => {
    const packageId = pkg.id;
    const previousValue = pkg.enforce_payment_before_shipping !== false;

    setEnforcePaymentToggleLoading(prev => ({ ...prev, [packageId]: true }));
    setPackages(prev =>
      prev.map(item =>
        item.id === packageId
          ? { ...item, enforce_payment_before_shipping: checked }
          : item
      )
    );

    try {
      await updatePackage(packageId, {
        enforce_payment_before_shipping: checked
      });
      setSnackbar({ open: true, message: t('packages:messages.packageUpdatedSuccess'), severity: 'success' });
    } catch (error) {
      setPackages(prev =>
        prev.map(item =>
          item.id === packageId
            ? { ...item, enforce_payment_before_shipping: previousValue }
            : item
        )
      );
      setSnackbar({ open: true, message: t('packages:messages.failedToUpdatePackage'), severity: 'error' });
    } finally {
      setEnforcePaymentToggleLoading(prev => {
        const next = { ...prev };
        delete next[packageId];
        return next;
      });
    }
  };

  const handleViewPoolDetails = async (poolSid) => {
    try {
      setPoolDetailsLoading(true);
      const pool = await getPoolDetails(poolSid);
      setPoolDetails(pool);
      setPoolDetailsDialogOpen(true);
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to load pool details', 
        severity: 'error' 
      });
    } finally {
      setPoolDetailsLoading(false);
    }
  };

  const parsePackageDimensions = (rawValue) => {
    const raw = String(rawValue || '').trim();
    if (!raw) return [];

    const normalized = raw
      .replace(/[;,|]+/g, '\n')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    const parsed = [];
    for (const line of normalized) {
      const parts = line
        .replace(/\s+/g, '')
        .split(/[xX*]/g)
        .map((p) => p.trim())
        .filter(Boolean);

      if (parts.length < 3) continue;
      const length = parseFloat(parts[0]);
      const width = parseFloat(parts[1]);
      const height = parseFloat(parts[2]);
      if ([length, width, height].some((n) => Number.isNaN(n) || n <= 0)) continue;
      parsed.push({ length, width, height });
    }

    return parsed;
  };

  const formatDimension = ({ length, width, height }) => {
    const formatNumber = (value) => {
      const str = String(value);
      if (str.includes('.')) {
        const trimmed = value.toFixed(4).replace(/\.?0+$/, '');
        return trimmed;
      }
      return String(value);
    };
    return `${formatNumber(length)}x${formatNumber(width)}x${formatNumber(height)}`;
  };

  const normalizePackageDimensionText = (rawValue) => {
    const dims = parsePackageDimensions(rawValue);
    if (!dims.length) return '';
    return dims.map(formatDimension).join('\n');
  };

  const calculateCBM = (dimensionStr) => {
    const dims = parsePackageDimensions(dimensionStr);
    if (!dims.length) return '';
    const total = dims.reduce((sum, d) => sum + (d.length * d.width * d.height) / 1000000, 0);
    return total.toFixed(4);
  };

  const handleEditClick = async (pkg) => {
    setSelectedPackage(pkg);
    setEditWeight(pkg.package_weight || '');
    setEditCbm(pkg.package_cbm || '');
    setEditDimension(pkg.package_dimension || '');
    setEditCarton(pkg.carton ?? '');
    setEditShippingMethod(pkg.shipping_method || '');
    setEditShippingCost(pkg.international_shipping_cost ?? '');
    setEditPackingFee(pkg.packing_fee ?? '');
    setEditPickupOption(pkg.pickup_option || 'platform_pickup');
    setEditLogisticsId(pkg.selected_logistics?.id || '');
    setEditEnforcePaymentBeforeShipping(pkg.enforce_payment_before_shipping !== false);
    setEditDialogOpen(true);
    
    // Load logistics if shipping method is set
    if (pkg.shipping_method) {
      await loadLogisticsForMethod(pkg.shipping_method);
    }
  };
  
  const loadLogisticsForMethod = async (method) => {
    if (!method) {
      setAvailableLogistics([]);
      return;
    }
    
    setLogisticsLoading(true);
    try {
      const response = await fetchLogistics({ method, page_size: 100 }); // Get all logistics for the method
      // Handle both paginated and non-paginated responses
      const logisticsList = response.results || (Array.isArray(response) ? response : []);
      setAvailableLogistics(logisticsList);
    } catch (error) {
      console.error('Error loading logistics:', error);
      setAvailableLogistics([]);
      setSnackbar({ open: true, message: 'Failed to load logistics providers', severity: 'error' });
    } finally {
      setLogisticsLoading(false);
    }
  };

  const handleEditDimensionChange = (value) => {
    setEditDimension(value);
    setEditShippingCost('');
    // Auto-calculate CBM when dimension changes
    const calculatedCbm = calculateCBM(value);
    if (calculatedCbm) {
      setEditCbm(calculatedCbm);
    }
  };

  const handleEditCartonChange = (value) => {
    setEditCarton(value);
  };

  // Calculate shipping cost based on weight/volume, shipping method, and logistics provider
  const calculateShippingCost = async (method, weight, cbm, logisticsId = null) => {
    if (!method || (!weight && !cbm) || !selectedPackage?.id) {
      return null;
    }
    
    // If logistics not selected, can't calculate
    const logisticsToUse = logisticsId || editLogisticsId;
    if (!logisticsToUse) {
      return null;
    }

    try {
      // Use the api service directly
      const response = await api.post(
        `/admin/packages/${selectedPackage.id}/calculate-shipping-cost/`,
        {
          shipping_method: method,
          package_weight: parseFloat(weight) || 0,
          package_cbm: parseFloat(cbm) || 0,
          package_dimension: normalizePackageDimensionText(editDimension),
          logistics_id: logisticsToUse,
          packing_fee: editPackingFee === '' ? null : editPackingFee,
        }
      );

      if (response.data?.success) {
        return response.data.estimated_cost ?? null;
      }
    } catch (error) {
      console.error('Error calculating shipping cost:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to calculate shipping cost';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }

    return null;
  };

  const handleShippingMethodChange = async (method) => {
    setEditShippingMethod(method);
    setEditLogisticsId(''); // Reset logistics selection
    setEditShippingCost(''); // Clear cost
    
    // Load available logistics for the method
    await loadLogisticsForMethod(method);
  };
  
  const handleLogisticsChange = async (logisticsId) => {
    setEditLogisticsId(logisticsId);
    
    // Auto-calculate cost when logistics is selected and weight/CBM are available
    if (logisticsId && editShippingMethod && (editShippingMethod === 'air' ? editWeight : editCbm)) {
      const calculatedCost = await calculateShippingCost(editShippingMethod, editWeight, editCbm, logisticsId);
      if (calculatedCost !== null) {
        setEditShippingCost(calculatedCost.toFixed(2));
      }
    }
  };

  const handleCalculateCost = async () => {
    if (!editShippingMethod) {
      setSnackbar({ open: true, message: 'Please select a shipping method first', severity: 'warning' });
      return;
    }
    if (!editLogisticsId) {
      setSnackbar({ open: true, message: 'Please select a logistics provider first', severity: 'warning' });
      return;
    }
    if (editShippingMethod === 'air' ? !editWeight : !editCbm) {
      setSnackbar({ open: true, message: 'Please enter weight for air or CBM for sea shipping', severity: 'warning' });
      return;
    }
    
    const calculatedCost = await calculateShippingCost(editShippingMethod, editWeight, editCbm, editLogisticsId);
    if (calculatedCost !== null) {
      setEditShippingCost(calculatedCost.toFixed(2));
      setSnackbar({ open: true, message: 'Shipping cost calculated successfully', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Failed to calculate shipping cost', severity: 'error' });
    }
  };

  const handleEditSubmit = async () => {
    if (editPackingFee !== '' && (!Number.isFinite(Number(editPackingFee)) || Number(editPackingFee) < 0)) {
      setSnackbar({ open: true, message: 'Packing fee must be zero or a positive USD amount.', severity: 'error' });
      return;
    }
    if (!editWeight || !editCbm) {
      setSnackbar({ open: true, message: t('packages:messages.pleaseEnterWeightAndCbm'), severity: 'warning' });
      return;
    }

    setEditLoading(true);
    try {
      const normalizedDimension = normalizePackageDimensionText(editDimension);
      const dimensionDerivedCbm = calculateCBM(normalizedDimension);

      const updateData = {
        package_weight: parseFloat(editWeight),
        package_cbm: dimensionDerivedCbm ? parseFloat(dimensionDerivedCbm) : parseFloat(editCbm),
        package_dimension: normalizedDimension || editDimension,
        carton: editCarton === '' ? null : parseInt(editCarton, 10),
        shipping_method: editShippingMethod || null,
        packing_fee: editPackingFee === '' ? null : editPackingFee,
        pickup_option: editPickupOption || 'platform_pickup',
        selected_logistics_id: editLogisticsId || null,
        enforce_payment_before_shipping: editEnforcePaymentBeforeShipping
      };
      
      // If setting shipping method and no option selected, default to platform
      if (editShippingMethod && !selectedPackage.shipping_option) {
        updateData.shipping_option = 'platform';
      }

      const response = await updatePackage(selectedPackage.id, updateData);

      if (response) {
        setSnackbar({ open: true, message: t('packages:messages.packageUpdatedSuccess'), severity: 'success' });
        loadPackages();
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error('Error updating package:', error);
      setSnackbar({ open: true, message: t('packages:messages.failedToUpdatePackage'), severity: 'error' });
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedPackage(null);
    setEditPackingFee('');
    setEditWeight('');
    setEditCbm('');
    setEditDimension('');
    setEditCarton('');
    setEditShippingMethod('');
    setEditShippingCost('');
    setEditPickupOption('platform_pickup');
    setEditLogisticsId('');
    setAvailableLogistics([]);
    setEditEnforcePaymentBeforeShipping(true);
  };

  // Split Package Handlers
  const handleSplitClick = async (pkg) => {
    try {
      const details = await fetchPackageById(pkg.id);
      setSelectedPackage(details);
      setSelectedOrderIds([]);
      setSplitDialogOpen(true);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load package details', severity: 'error' });
    }
  };

  const handleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const getSplittableOrders = (pkg) => (pkg?.orders || []).filter((order) => SPLITTABLE_ORDER_STATUSES.includes(order.status));

  const canSplitPackageFromAdminView = (pkg) => Boolean(pkg) && !['shipped', 'picked_up', 'completed'].includes(pkg.packaging_status);

  const submitPackageSplit = async ({ packageData, orderIds, closeDialog = false }) => {
    if (!packageData) return;

    if (orderIds.length === 0) {
      setSnackbar({ open: true, message: t('packages:messages.pleaseSelectAtLeastOneOrder'), severity: 'warning' });
      return;
    }

    const activeOrders = getSplittableOrders(packageData);
    if (orderIds.length >= activeOrders.length) {
      setSnackbar({ open: true, message: t('packages:messages.mustLeaveAtLeastOneOrder'), severity: 'warning' });
      return;
    }

    setSplitLoading(true);
    try {
      const response = await splitPackage(packageData.id, orderIds);
      if (response.success) {
        setSnackbar({ open: true, message: response.message, severity: 'success' });
        setSelectedOrderIds([]);
        if (response.original_package) {
          setSelectedPackage(response.original_package);
        }
        if (closeDialog) {
          setSplitDialogOpen(false);
        }
        loadPackages();
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t('packages:messages.failedToAutoSplit'), severity: 'error' });
    } finally {
      setSplitLoading(false);
    }
  };

  const handleSplitSubmit = async () => {
    await submitPackageSplit({ packageData: selectedPackage, orderIds: selectedOrderIds, closeDialog: true });
  };

  const handleAutoSplit = async () => {
    if (!selectedPackage) return;

    if (selectedPackage.package_type !== 'mixed') {
      setSnackbar({ open: true, message: t('packages:messages.packageAlreadyType', { type: selectedPackage.package_type }), severity: 'warning' });
      return;
    }

    if (!window.confirm(t('packages:messages.autoSplitConfirm'))) return;

    setSplitLoading(true);
    try {
      const response = await splitPackageByGoodsType(selectedPackage.id);
      if (response.success) {
        setSnackbar({ open: true, message: response.message, severity: 'success' });
        setSelectedOrderIds([]);
        if (response.original_package) {
          setSelectedPackage(response.original_package);
        }
        setSplitDialogOpen(false);
        loadPackages();
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t('packages:messages.failedToAutoSplit'), severity: 'error' });
    } finally {
      setSplitLoading(false);
    }
  };

  // Merge Packages Handler
  const handleMergePackages = async () => {
    if (selectedPackageIds.length < 2) {
      setSnackbar({ open: true, message: t('packages:messages.pleaseSelectAtLeastTwoPackages'), severity: 'warning' });
      return;
    }

    if (selectedMergeBlockedPackages.length > 0) {
      setSnackbar({
        open: true,
        message: 'Only pre-shipment packages can be merged. Unselect shipped, in-Nigeria, picked-up, completed, or disputed packages first.',
        severity: 'warning'
      });
      return;
    }

    if (!window.confirm(t('packages:messages.mergeConfirm', { count: selectedPackageIds.length }))) return;

    setMergeLoading(true);
    try {
      const response = await mergePackages(selectedPackageIds);
      if (response.success) {
        setSnackbar({ open: true, message: response.message, severity: 'success' });
        setSelectedPackageIds([]);
        loadPackages();
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || t('packages:messages.failedToMergePackages'), severity: 'error' });
    } finally {
      setMergeLoading(false);
    }
  };

  // Dispute handlers
  const handleDisputeClick = async (pkg) => {
    try {
      setDisputeLoading(true);
      const details = await fetchPackageById(pkg.id);
      setDisputePackage(details);
      setDisputeData(details.dispute);
      setDisputeReply('');
      setDisputeReplyImages([]);
      setDisputeResolutionNotes('');
      setDisputeDialogOpen(true);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load dispute details', severity: 'error' });
    } finally {
      setDisputeLoading(false);
    }
  };

  const handleDisputeReplySubmit = async () => {
    if (!disputeReply.trim()) {
      setSnackbar({ open: true, message: 'Please enter a reply message', severity: 'warning' });
      return;
    }

    setDisputeReplyLoading(true);
    try {
      await replyDispute(disputePackage.id, disputeReply, disputeReplyImages);
      setSnackbar({ open: true, message: 'Reply sent successfully', severity: 'success' });
      setDisputeReply('');
      setDisputeReplyImages([]);
      // Reload dispute data
      const details = await fetchPackageById(disputePackage.id);
      setDisputeData(details.dispute);
      loadPackages();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to send reply', severity: 'error' });
    } finally {
      setDisputeReplyLoading(false);
    }
  };

  const handleDisputeImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadFile(formData);
      const imageUrl = response.url || response.file_url;
      if (imageUrl) {
        setDisputeReplyImages(prev => [...prev, imageUrl]);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to upload image', severity: 'error' });
    }
  };

  const handleResolveDispute = async () => {
    if (!disputePackage || !disputeData) return;

    // Optional: Ask for confirmation
    if (!window.confirm('Are you sure you want to resolve this dispute? This action cannot be undone.')) {
      return;
    }

    setDisputeResolveLoading(true);
    try {
      // Use the reply message if available, otherwise use resolution notes
      const resolutionMessage = disputeReply.trim() || disputeResolutionNotes.trim() || 'Dispute resolved by admin';
      
      await resolveDispute(
        disputePackage.id,
        disputeResolutionNotes.trim() || 'Dispute resolved',
        resolutionMessage,
        disputeReplyImages
      );
      
      setSnackbar({ open: true, message: 'Dispute resolved successfully', severity: 'success' });
      
      // Reload dispute data
      const details = await fetchPackageById(disputePackage.id);
      setDisputeData(details.dispute);
      setDisputeReply('');
      setDisputeReplyImages([]);
      setDisputeResolutionNotes('');
      loadPackages();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to resolve dispute', severity: 'error' });
    } finally {
      setDisputeResolveLoading(false);
    }
  };

  // Cost Distribution Handlers
  const handleDistributeClick = (pkg) => {
    setSelectedPackage(pkg);
    setDistributeTotalCost('');
    setDistributeDueDate('');
    setDistributeDialogOpen(true);
  };

  const handleDistributeSubmit = async () => {
    if (!distributeTotalCost || isNaN(parseFloat(distributeTotalCost))) {
      setSnackbar({ open: true, message: 'Please enter a valid total cost', severity: 'warning' });
      return;
    }

    setDistributeLoading(true);
    try {
      const response = await distributeShippingCosts(selectedPackage.id, parseFloat(distributeTotalCost), distributeDueDate);
      if (response.success) {
        setSnackbar({ open: true, message: response.message, severity: 'success' });
        setDistributeDialogOpen(false);
        setDistributeTotalCost('');
        setDistributeDueDate('');
        loadPackages();
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to distribute costs', severity: 'error' });
    } finally {
      setDistributeLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'ready_to_package': return 'success';
      case 'packaging': return 'info';
      case 'packaged': return 'secondary';
      case 'shipped': return 'error';
      case 'picked_up': return 'default';
      case 'disputed': return 'error';
      case 'dispute_resolved': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusChipProps = (status, sx = {}) => {
    if (status === 'picked_up') {
      return {
        sx: {
          backgroundColor: '#7C3AED',
          color: '#FFFFFF',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#6D28D9',
          },
          ...sx,
        },
      };
    }

    return {
      color: getStatusColor(status),
      sx,
    };
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return t('packages:status.pending');
      case 'ready_to_package': return t('packages:status.readyToPackage');
      case 'packaging': return t('packages:status.packaging');
      case 'packaged': return t('packages:status.packaged');
      case 'shipped': return t('packages:status.shipped');
      case 'picked_up': return t('packages:status.pickedUp', { default: 'Picked Up' });
      case 'disputed': return t('packages:status.disputed', { default: 'Disputed' });
      case 'dispute_resolved': return t('packages:status.disputeResolved', { default: 'Dispute Resolved' });
      case 'completed': return t('packages:status.completed', { default: 'Completed' });
      default: return status;
    }
  };

  const getShippingOptionLabel = (option) => {
    if (option === 'self') return t('packages:shippingOption.self');
    if (option === 'platform') return t('packages:shippingOption.platform');
    return t('packages:shippingOption.notSelected');
  };

  const getShippingOptionColor = (option) => {
    if (option === 'self') return 'warning';
    if (option === 'platform') return 'primary';
    return 'default';
  };

  const getPickupOptionLabel = (option) => {
    if (option === 'self_pickup') return 'Self Pickup';
    return 'Platform Pickup';
  };

  const getPickupOptionColor = (option) => {
    if (option === 'self_pickup') return 'warning';
    return 'primary';
  };

  const getLocalLogisticsPaymentStatus = (pkg) => {
    if (pkg.local_logistics_payment_status) return pkg.local_logistics_payment_status;
    return pkg.local_logistics_fee_paid ? 'paid' : 'unpaid';
  };

  const getLocalLogisticsPaymentMeta = (pkg) => {
    const paymentStatus = getLocalLogisticsPaymentStatus(pkg);
    if (paymentStatus === 'paid') {
      return { label: t('packages:table.paid'), color: 'success' };
    }
    if (paymentStatus === 'not_required') {
      return { label: t('packages:table.notRequired', { default: 'Not Required' }), color: 'default' };
    }
    return { label: t('packages:table.unpaid'), color: 'warning' };
  };

  const isPackageShippingPaid = (pkg) => Boolean(
    pkg?.international_shipping_fee_paid ||
    pkg?.local_logistics_fee_paid ||
    pkg?.paid_shipping_display_amount ||
    pkg?.pool_info?.payment_status === 'paid'
  );

  const normalizeFeeInput = (input) => {
    const rawValue = String(input ?? '').trim();
    if (!rawValue) return null;

    const amount = Number.parseFloat(rawValue.replace(/[,\s₦¥]/g, ''));
    if (!Number.isFinite(amount) || amount < 0) {
      throw new Error('Enter a valid fee amount');
    }
    return amount.toFixed(2);
  };

  const handleLogisticsFeeCommit = async (pkg, field, inputValue) => {
    if (isPackageShippingPaid(pkg)) {
      setSnackbar({
        open: true,
        message: 'Paid package fees cannot be edited',
        severity: 'warning'
      });
      return;
    }

    let normalizedValue;
    try {
      normalizedValue = normalizeFeeInput(inputValue);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'warning' });
      return;
    }

    const currentValue = pkg[field] === null || pkg[field] === undefined || pkg[field] === ''
      ? null
      : Number.parseFloat(pkg[field]).toFixed(2);
    if (normalizedValue === currentValue) return;

    const savingKey = `${pkg.id}-${field}`;
    setFeeSaving(prev => ({ ...prev, [savingKey]: true }));

    try {
      const response = await updatePackageLogisticsFees(pkg.id, { [field]: normalizedValue });
      const updatedPackage = response.package || response;

      setPackages(prev =>
        prev.map(item =>
          item.id === pkg.id
            ? { ...item, ...updatedPackage }
            : item
        )
      );
      setSelectedPackage(prev =>
        prev?.id === pkg.id
          ? { ...prev, ...updatedPackage }
          : prev
      );
      setSnackbar({ open: true, message: response.message || 'Logistics fee updated', severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.response?.data?.detail || 'Failed to update logistics fee',
        severity: 'error'
      });
    } finally {
      setFeeSaving(prev => {
        const next = { ...prev };
        delete next[savingKey];
        return next;
      });
    }
  };

  const formatNaira = (amount) => {
    const numeric = Number.parseFloat(amount || 0);
    if (Number.isNaN(numeric)) return '₦0.00';
    return `₦${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getTransferReceiptName = (approval) => {
    if (!approval) return '';
    if (approval.receipt_name) return approval.receipt_name;
    if (!approval.receipt_url) return '';
    return approval.receipt_url.split('/').pop() || '';
  };

  const canShowTransferPaymentAction = (pkg) => {
    const localOutstanding = !!pkg.local_logistics_fee && getLocalLogisticsPaymentStatus(pkg) === 'unpaid';
    const poolInternationalOutstanding = !!pkg.pool_info && pkg.pool_info.payment_status !== 'paid' && !!pkg.international_shipping_cost;
    const directInternationalOutstanding = !pkg.international_shipping_fee_paid;

    return localOutstanding || poolInternationalOutstanding || directInternationalOutstanding;
  };

  const getOrderStatusMeta = (status) => {
    switch (status) {
      case 3:
        return { label: t('packages:orderStatus.purchased'), color: 'warning' };
      case 4:
        return { label: t('packages:orderStatus.inWarehouse'), color: 'success' };
      case 5:
        return { label: t('packages:orderStatus.shippedOut'), color: 'primary' };
      case 6:
        return { label: t('packages:orderStatus.inNigeria'), color: 'info' };
      case 7:
        return { label: t('packages:orderStatus.received'), color: 'success' };
      case 9:
        return { label: t('packages:orderStatus.success'), color: 'success' };
      default:
        return { label: t('packages:orderStatus.pending'), color: 'default' };
    }
  };

  const columns = [
    {
      field: 'select',
      headerName: '',
      width: 60,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          checked={selectedPackageIds.length > 0 && selectedPackageIds.length === packages.filter(p => SELECTABLE_PACKAGE_STATUSES.includes(p.packaging_status)).length}
          indeterminate={selectedPackageIds.length > 0 && selectedPackageIds.length < packages.filter(p => SELECTABLE_PACKAGE_STATUSES.includes(p.packaging_status)).length}
          onChange={(e) => {
            if (e.target.checked) {
              const selectableIds = packages.filter(p => SELECTABLE_PACKAGE_STATUSES.includes(p.packaging_status)).map(p => p.id);
              setSelectedPackageIds(selectableIds);
            } else {
              setSelectedPackageIds([]);
            }
          }}
        />
      ),
      renderCell: (params) => {
        if (!SELECTABLE_PACKAGE_STATUSES.includes(params.row.packaging_status)) return null;
        return (
          <Checkbox
            checked={selectedPackageIds.includes(params.row.id)}
            onChange={() => {
              setSelectedPackageIds(prev =>
                prev.includes(params.row.id)
                  ? prev.filter(id => id !== params.row.id)
                  : [...prev, params.row.id]
              );
            }}
          />
        );
      }
    },
    { field: 'sn', headerName: t('packages:table.sn'), width: 70, sortable: false, filterable: false },
    {
      field: 'package_number',
      headerName: t('packages:table.packageNumber'),
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="600">
            {params.row.package_number}
          </Typography>
          {params.row.is_fulfillment_package && (
            <Chip label="Fulfillment" size="small" color="secondary" variant="outlined" sx={{ height: 20, mt: 0.5 }} />
          )}
        </Box>
      )
    },
    {
      field: 'is_demo',
      headerName: t('packages:table.packageMode'),
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('packages:mode.staffDemo') : t('packages:mode.livePackage')}
          size="small"
          color={params.value ? 'warning' : 'success'}
          variant={params.value ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    {
      field: 'user_info',
      headerName: t('packages:table.customer'),
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="500">
            {params.row.user_info?.username || 'N/A'}
          </Typography>
          {params.row.user_info?.bin_code && (
            <Typography variant="caption" color="text.secondary">
              BIN: {params.row.user_info.bin_code}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'packaging_status',
      headerName: t('packages:table.status'),
      width: 180,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.row.packaging_status)}
          size="small"
          {...getStatusChipProps(params.row.packaging_status)}
        />
      )
    },
    {
      field: 'shipping_option',
      headerName: t('packages:table.shippingOption'),
      width: 180,
      renderCell: (params) => {
        const option = params.row.shipping_option;
        const label = getShippingOptionLabel(option);
        const color = getShippingOptionColor(option);

        if (!option) {
          return <Chip label={label} size="small" variant="outlined" />;
        }

        return (
          <Chip
            label={label}
            color={color}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'pickup_option',
      headerName: 'Pickup Mode',
      width: 160,
      renderCell: (params) => (
        <Chip
          label={getPickupOptionLabel(params.row.pickup_option)}
          color={getPickupOptionColor(params.row.pickup_option)}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'orders_progress',
      headerName: t('packages:table.orders'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.orders_in_warehouse}/{params.row.total_orders}
        </Typography>
      )
    },
    {
      field: 'is_group_buy',
      headerName: 'Group Buy',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_group_buy ? 'Yes' : 'No'}
          color={params.row.is_group_buy ? 'secondary' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'pool_info',
      headerName: 'Pool',
      width: 180,
      renderCell: (params) => {
        const poolInfo = params.row.pool_info;
        if (!poolInfo) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        return (
          <Box>
            <Chip
              label={poolInfo.pool_id}
              size="small"
              color="info"
              sx={{ mb: 0.5 }}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              {poolInfo.pool_status} • {poolInfo.shipping_method === 'air' ? 'Air' : 'Sea'}
            </Typography>
            {poolInfo.payment_status && (
              <Chip
                label={poolInfo.payment_status}
                size="small"
                color={poolInfo.payment_status === 'paid' ? 'success' : 'warning'}
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
        );
      }
    },
    {
      field: 'package_weight',
      headerName: t('packages:table.weight'),
      width: 120,
      renderCell: (params) => params.row.package_weight || '—'
    },
    {
      field: 'package_cbm',
      headerName: t('packages:table.cbm'),
      width: 120,
      renderCell: (params) => params.row.package_cbm || '—'
    },
    {
      field: 'package_dimension',
      headerName: t('packages:table.dimension'),
      width: 140,
      renderCell: (params) => {
        const dims = parsePackageDimensions(params.row.package_dimension);
        if (!dims.length) return '—';
        const totalCbm = dims.reduce((sum, d) => sum + (d.length * d.width * d.height) / 1000000, 0).toFixed(4);
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-line', display: 'block', lineHeight: 1.2 }}>
              {dims.map(formatDimension).join('\n')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              Total: {totalCbm} m³
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'carton',
      headerName: 'Carton',
      width: 110,
      renderCell: (params) => (params.row.carton ?? '—')
    },
    {
      field: 'local_logistics_chargeable_weight',
      headerName: t('packages:table.chargeableWeight'),
      width: 140,
      renderCell: (params) => params.row.local_logistics_chargeable_weight 
        ? `${params.row.local_logistics_chargeable_weight} kg` 
        : '—'
    },
    {
      field: 'local_logistics_fee',
      headerName: t('packages:table.localLogisticsFee'),
      width: 150,
      renderCell: (params) => (
        <EditableFeeCell
          row={params.row}
          field="local_logistics_fee"
          prefix="¥"
          saving={!!feeSaving[`${params.row.id}-local_logistics_fee`]}
          disabled={isPackageShippingPaid(params.row)}
          onCommit={handleLogisticsFeeCommit}
        />
      )
    },
    {
      field: 'nga_logistics_fee',
      headerName: t('packages:table.ngaLogisticsFee'),
      width: 160,
      renderCell: (params) => (
        <EditableFeeCell
          row={params.row}
          field="nga_logistics_fee"
          prefix="₦"
          saving={!!feeSaving[`${params.row.id}-nga_logistics_fee`]}
          disabled={isPackageShippingPaid(params.row)}
          onCommit={handleLogisticsFeeCommit}
        />
      )
    },
    {
      field: 'international_shipping_cost',
      headerName: t('packages:table.internationalShippingCost'),
      width: 180,
      renderCell: (params) => params.row.international_shipping_cost
        ? `₦${parseFloat(params.row.international_shipping_cost).toFixed(2)}`
        : '—'
    },
    {
      field: 'international_shipping_fee_paid',
      headerName: 'International Payment',
      width: 180,
      renderCell: (params) => {
        if (!params.row.international_shipping_cost) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        const isPaid = params.row.international_shipping_fee_paid === true;
        const isConfirmed = params.row.international_shipping_fee_status === 'confirmed';
        return (
          <Chip
            label={isPaid ? 'Paid' : (isConfirmed ? 'Final / Unpaid' : 'Estimate')}
            color={isPaid ? 'success' : (isConfirmed ? 'warning' : 'default')}
            size="small"
          />
        );
      }
    },
    {
      field: 'local_logistics_fee_paid',
      headerName: t('packages:table.localLogisticsPaymentStatus'),
      width: 160,
      renderCell: (params) => {
        if (!params.row.local_logistics_fee) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        const paymentMeta = getLocalLogisticsPaymentMeta(params.row);
        return (
          <Chip
            label={paymentMeta.label}
            color={paymentMeta.color}
            size="small"
          />
        );
      }
    },
    {
      field: 'enforce_payment_before_shipping',
      headerName: t('packages:table.enforcePaymentBeforeShipping'),
      width: 190,
      renderCell: (params) => {
        const checked = params.row.enforce_payment_before_shipping !== false;
        const isLoading = !!enforcePaymentToggleLoading[params.row.id];
        return (
          <Switch
            checked={checked}
            color="error"
            size="small"
            disabled={isLoading}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              handleEnforcePaymentToggle(params.row, e.target.checked);
            }}
          />
        );
      }
    },
    {
      field: 'shipping_method',
      headerName: t('packages:table.shipping'),
      width: 120,
      renderCell: (params) => params.row.shipping_method ? (
        <Chip
          label={params.row.shipping_method === 'sea' ? t('packages:shippingMethod.sea') : t('packages:shippingMethod.air')}
          size="small"
          variant="outlined"
        />
      ) : '—'
    },
    {
      field: 'created_at',
      headerName: t('packages:table.created'),
      width: 150,
      renderCell: (params) => new Date(params.row.created_at).toLocaleDateString()
    },
    {
      field: 'actions',
      headerName: t('packages:table.actions'),
      width: 460,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('packages:tooltips.viewDetails')}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewDetails(params.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          
          {params.row.pool_info && (
            <Tooltip title={`View Pool Details: ${params.row.pool_info.pool_id}`}>
              <IconButton
                size="small"
                color="info"
                onClick={() => handleViewPoolDetails(params.row.pool_info.pool_sid)}
              >
                <Groups />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title={t('packages:tooltips.editPackageInfo')}>
            <IconButton
              size="small"
              sx={{ color: '#FF9800' }}
              onClick={() => handleEditClick(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          
          {/* Keep upload available for additional package proof photos */}
          {['packaging', 'packaged'].includes(params.row.packaging_status) && (
            <Tooltip title={t('packages:tooltips.uploadPackageInfo')}>
              <IconButton
                size="small"
                sx={{ color: '#4CAF50' }}
                onClick={() => handleUploadClick(params.row)}
              >
                <CloudUpload />
              </IconButton>
            </Tooltip>
          )}
          
          {params.row.packaging_status === 'packaged' && 
           params.row.local_logistics_fee && 
           ['paid', 'not_required'].includes(getLocalLogisticsPaymentStatus(params.row)) &&
           params.row.shipping_option &&
           params.row.shipping_method && (
            <Tooltip title={t('packages:tooltips.markAsShipped')}>
              <IconButton
                size="small"
                sx={{ color: '#DC143C' }}
                onClick={() => handleShip(params.row)}
              >
                <LocalShipping />
              </IconButton>
            </Tooltip>
          )}

          {params.row.packaging_status === 'shipped' && !params.row.pickup_available_at && (
            <Tooltip title="Mark Available for Pickup">
              <span>
                <IconButton
                  size="small"
                  sx={{ color: '#1976D2' }}
                  onClick={() => handleMarkPickupAvailable(params.row)}
                  disabled={!!pickupAvailableLoading[params.row.id]}
                >
                  {pickupAvailableLoading[params.row.id] ? <CircularProgress size={18} /> : <CheckCircle />}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {params.row.packaging_status === 'shipped' && params.row.pickup_available_at && (
            <Tooltip title={`Pickup available. Auto-confirm scheduled for ${new Date(params.row.pickup_auto_confirm_at).toLocaleString()}`}>
              <CheckCircle sx={{ color: '#4CAF50', alignSelf: 'center' }} />
            </Tooltip>
          )}

          {params.row.packaging_status === 'shipped' && params.row.pickup_available_at && (
            <Tooltip title="Resend Pickup Message">
              <span>
                <IconButton
                  size="small"
                  sx={{ color: '#7B1FA2' }}
                  onClick={() => handleResendPickupMessage(params.row)}
                  disabled={!!resendPickupMessageLoading[params.row.id]}
                >
                  {resendPickupMessageLoading[params.row.id] ? <CircularProgress size={18} /> : <Sms />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          
          {!['shipped', 'picked_up', 'completed'].includes(params.row.packaging_status) && (
            <Tooltip title={t('packages:tooltips.splitPackage')}>
              <IconButton
                size="small"
                sx={{ color: '#9C27B0' }}
                onClick={() => handleSplitClick(params.row)}
              >
                <CallSplit />
              </IconButton>
            </Tooltip>
          )}

          {/* Distribute Costs Button for Shared Packages */}
          {params.row.is_shared && ['packaging', 'packaged'].includes(params.row.packaging_status) && (
            <Tooltip title="Distribute Shipping Costs">
              <IconButton
                size="small"
                sx={{ color: '#009688' }}
                onClick={() => handleDistributeClick(params.row)}
              >
                <Calculate />
              </IconButton>
            </Tooltip>
          )}

          {canShowTransferPaymentAction(params.row) && (
            <Tooltip title="Pay with Transfer">
              <IconButton
                size="small"
                sx={{ color: 'success.main' }}
                onClick={() => handleOpenTransferPaymentDialog(params.row)}
              >
                <AccountBalance />
              </IconButton>
            </Tooltip>
          )}
          
          {params.row.dispute && (
            <Tooltip title={params.row.dispute.status === 'open' ? "View/Reply to Dispute" : "View Dispute"}>
              <IconButton
                size="small"
                sx={{ color: params.row.dispute.status === 'open' ? '#FF5722' : '#9E9E9E' }}
                onClick={() => handleDisputeClick(params.row)}
              >
                <Gavel />
              </IconButton>
            </Tooltip>
          )}

          {canDeletePackages && (
            <Tooltip title="Delete Package">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeletePackage(params.row)}
              >
                <DeleteForever />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="#DC143C" sx={{ mb: 2 }}>
          {t('packages:title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadPackages}
            disabled={loading}
          >
            {t('packages:refresh')}
          </Button>
          
          <Button
            variant="contained"
            startIcon={mergeLoading ? <CircularProgress size={20} color="inherit" /> : <MergeType />}
            onClick={handleMergePackages}
            disabled={selectedPackageIds.length < 2 || selectedMergeBlockedPackages.length > 0 || mergeLoading}
            sx={{ backgroundColor: '#9C27B0', '&:hover': { backgroundColor: '#7B1FA2' } }}
          >
            {t('packages:mergePackages', { count: selectedPackageIds.length })}
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={silentPickupLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            onClick={handleBulkMarkPickedUpSilently}
            disabled={selectedSilentPickupIds.length === 0 || silentPickupLoading}
          >
            Silent Picked Up ({selectedSilentPickupIds.length})
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                label={t('packages:filters.search')}
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                placeholder={t('packages:filters.searchPlaceholder')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('packages:filters.statusFilter')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('packages:filters.statusFilter')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('packages:filters.allStatuses')}</MenuItem>
                  <MenuItem value="pending">{t('packages:filters.pending')}</MenuItem>
                  <MenuItem value="ready_to_package">{t('packages:filters.readyToPackage')}</MenuItem>
                  <MenuItem value="packaging">{t('packages:filters.packaging')}</MenuItem>
                  <MenuItem value="packaged">{t('packages:filters.packaged')}</MenuItem>
                  <MenuItem value="shipped">{t('packages:filters.shipped')}</MenuItem>
                  <MenuItem value="picked_up">{t('packages:filters.pickedUp', { default: 'Picked Up' })}</MenuItem>
                  <MenuItem value="disputed">{t('packages:filters.disputed', { default: 'Disputed' })}</MenuItem>
                  <MenuItem value="dispute_resolved">{t('packages:filters.disputeResolved', { default: 'Dispute Resolved' })}</MenuItem>
                  <MenuItem value="completed">{t('packages:filters.completed', { default: 'Completed' })}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Pool Filter</InputLabel>
                <Select
                  value={inPoolFilter ? 'in_pool' : 'all'}
                  label="Pool Filter"
                  onChange={(e) => setInPoolFilter(e.target.value === 'in_pool')}
                >
                  <MenuItem value="all">All Packages</MenuItem>
                  <MenuItem value="in_pool">In Pool Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Packages Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={addSerialNumbers(packages, page, pageSize)}
              columns={columns}
              loading={loading}
              paginationMode="server"
              rowCount={totalPackages}
              page={page}
              pageSize={pageSize}
              hideFooterPagination
              hideFooterSelectedRowCount
              disableSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#DC143C',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.875rem',
                  py: 1,
                  alignItems: 'flex-start',
                  whiteSpace: 'normal',
                  lineHeight: 1.25,
                },
                '& .MuiDataGrid-columnSeparator': {
                  color: 'white'
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <CustomPagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        onCustomRowsInputChange={handleCustomRowsInputChange}
        onCustomRowsInputBlur={handleCustomRowsInputBlur}
      />

      {/* Upload Package Info Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('packages:dialogs.upload.title')}</Typography>
          <IconButton onClick={handleCloseUploadDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPackage && (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="600">
                {t('packages:dialogs.upload.package')} {selectedPackage.package_number}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('packages:dialogs.upload.customer')} {selectedPackage.user_info?.username} ({selectedPackage.total_orders} {t('packages:dialogs.upload.items')})
              </Typography>
              
              {/* Photo Upload */}
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>{t('packages:dialogs.upload.packagePhotos')}</Typography>
                <input
                  type="file"
                  multiple
                  accept={acceptedPackageImageTypes.join(',')}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="package-photos-input"
                  disabled={uploadLoading}
                />
                <Paper
                  component="label"
                  htmlFor="package-photos-input"
                  elevation={dragActive ? 6 : 1}
                  onDragEnter={handlePackageUploadDrag}
                  onDragLeave={handlePackageUploadDrag}
                  onDragOver={handlePackageUploadDrag}
                  onDrop={handlePackageUploadDrop}
                  sx={{
                    display: 'block',
                    p: 4,
                    mb: 2,
                    textAlign: 'center',
                    cursor: uploadLoading ? 'not-allowed' : 'pointer',
                    border: '2px dashed',
                    borderColor: dragActive ? 'success.main' : 'grey.300',
                    backgroundColor: dragActive ? '#E8F5E9' : '#fafafa',
                    opacity: uploadLoading ? 0.65 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: uploadLoading ? 'grey.300' : 'success.main',
                      backgroundColor: uploadLoading ? '#fafafa' : '#E8F5E9',
                    },
                  }}
                >
                  <CloudUpload sx={{ fontSize: 54, color: dragActive ? 'success.main' : 'grey.500', mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('packages:dialogs.upload.selectPhotos')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click to select or drag and drop multiple package proof images
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    JPEG, PNG, GIF, WebP • Max 10MB per image
                  </Typography>
                </Paper>
                
                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedFiles.length} image{selectedFiles.length !== 1 ? 's' : ''} selected
                    </Typography>
                    <Grid container spacing={2}>
                    {selectedFiles.map((file, index) => (
                      <Grid item xs={12} sm={6} md={4} key={`${file.name}-${index}`}>
                        <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                          <PackageImagePreview
                            file={file}
                            alt={file.name}
                            sx={{
                              width: '100%',
                              height: 140,
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                          <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={file.name}
                              onDelete={() => handleRemoveFile(index)}
                              size="small"
                              sx={{ flex: 1, maxWidth: '100%' }}
                            />
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                    </Grid>
                  </Box>
                )}
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                {t('packages:dialogs.upload.infoAlert')}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} disabled={uploadLoading}>
            {t('packages:dialogs.upload.cancel')}
          </Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            disabled={uploadLoading || selectedFiles.length === 0}
            startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUpload />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B01030' } }}
          >
            {uploadLoading ? t('packages:dialogs.upload.uploading') : t('packages:dialogs.upload.uploadPackageInfo')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Package Info Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="#DC143C" fontWeight="bold">
            {t('packages:dialogs.edit.title')}
          </Typography>
          <IconButton onClick={handleCloseEditDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPackage && (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="600">
                {t('packages:dialogs.edit.package')} {selectedPackage.package_number}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                {t('packages:dialogs.edit.customer')} {selectedPackage.user_info?.username}
              </Typography>
              
              <Stack spacing={2}>
                {/* Weight */}
                <TextField
                  label={t('packages:dialogs.edit.weight')}
                  type="number"
                  value={editWeight}
                  onChange={(e) => { setEditWeight(e.target.value); setEditShippingCost(''); }}
                  fullWidth
                  required
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText={t('packages:dialogs.edit.weightHelper')}
                />
                
                {/* Dimension */}
                <TextField
                  label={t('packages:dialogs.edit.dimension')}
                  type="text"
                  value={editDimension}
                  onChange={(e) => handleEditDimensionChange(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder={t('packages:dialogs.edit.dimensionPlaceholder')}
                  helperText={t('packages:dialogs.edit.dimensionHelper')}
                />

                <TextField
                  label="Carton"
                  type="number"
                  value={editCarton}
                  onChange={(e) => handleEditCartonChange(e.target.value)}
                  fullWidth
                  inputProps={{ min: '0', step: '1' }}
                  helperText="Number of cartons"
                />
                
                {/* CBM */}
                <TextField
                  label={t('packages:dialogs.edit.cbm')}
                  type="number"
                  value={editCbm}
                  onChange={(e) => { setEditCbm(e.target.value); setEditShippingCost(''); }}
                  fullWidth
                  required
                  disabled={parsePackageDimensions(editDimension).length > 0}
                  inputProps={{ step: '0.0001', min: '0' }}
                  helperText={t('packages:dialogs.edit.cbmHelper')}
                />
                
                <Typography variant="subtitle2" color="primary" sx={{ pt: 1 }}>
                  Shipping Details (Group Buy / Admin Override)
                </Typography>

                <FormControl fullWidth>
                  <InputLabel>Pickup Mode</InputLabel>
                  <Select
                    value={editPickupOption}
                    label="Pickup Mode"
                    onChange={(e) => setEditPickupOption(e.target.value)}
                  >
                    <MenuItem value="platform_pickup">Platform Pickup</MenuItem>
                    <MenuItem value="self_pickup">Self Pickup</MenuItem>
                  </Select>
                </FormControl>

                {editPickupOption === 'self_pickup' && (
                  <Alert severity="info">
                    NGA logistics fee will not apply to this package because the customer will pick it up directly from the shipping agent warehouse.
                  </Alert>
                )}

                {/* Shipping Method */}
                <FormControl fullWidth>
                  <InputLabel>{t('packages:table.shipping')}</InputLabel>
                  <Select
                    value={editShippingMethod}
                    label={t('packages:table.shipping')}
                    onChange={(e) => handleShippingMethodChange(e.target.value)}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="sea">{t('packages:shippingMethod.sea')}</MenuItem>
                    <MenuItem value="air">{t('packages:shippingMethod.air')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={(
                    <Switch
                      checked={editEnforcePaymentBeforeShipping}
                      onChange={(e) => setEditEnforcePaymentBeforeShipping(e.target.checked)}
                      color="error"
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {t('packages:dialogs.edit.enforcePaymentBeforeShipping')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('packages:dialogs.edit.enforcePaymentBeforeShippingHelper')}
                      </Typography>
                    </Box>
                  )}
                />

                {/* Logistics Provider Selection - Show only when shipping method is selected */}
                {editShippingMethod && (
                  <FormControl fullWidth>
                    <InputLabel>Logistics Provider</InputLabel>
                    {logisticsLoading ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Select
                        value={editLogisticsId}
                        label="Logistics Provider"
                        onChange={(e) => handleLogisticsChange(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Select Logistics Provider</em>
                        </MenuItem>
                        {availableLogistics.map((logistics) => (
                          <MenuItem key={logistics.id} value={logistics.id}>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {logistics.shipping_agent}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {logistics.terminal_port} • {logistics.goods_type}
                                {logistics.total_fee && ` • ₦${parseFloat(logistics.total_fee).toFixed(2)}/${logistics.method === 'air' ? 'kg' : 'CBM'}`}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    {availableLogistics.length === 0 && !logisticsLoading && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        No logistics providers available for {editShippingMethod} shipping
                      </Typography>
                    )}
                  </FormControl>
                )}

                <TextField
                  label="Packing Fee (USD) - Optional"
                  type="number"
                  value={editPackingFee}
                  disabled={selectedPackage?.international_shipping_fee_paid === true}
                  onChange={(e) => { setEditPackingFee(e.target.value); setEditShippingCost(''); }}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="Leave empty when not applicable. Added once to this package's international fee using the selected logistics dollar rate."
                />
                {/* Shipping Cost */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label="Estimated Shipping Cost (₦)"
                    type="number"
                    value={editShippingCost}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    inputProps={{ step: '0.01', min: '0' }}
                    helperText={
                      !editShippingMethod 
                        ? "Select shipping method first" 
                        : !editLogisticsId 
                        ? "Select logistics provider and click Calculate" 
                        : "Calculated international fee including packing; recalculated when saved"
                    }
                  />
                  {editShippingMethod && editLogisticsId && (editShippingMethod === 'air' ? editWeight : editCbm) && (
                    <Button
                      variant="outlined"
                      startIcon={<Calculate />}
                      onClick={handleCalculateCost}
                      sx={{ mt: 1, minWidth: 'auto' }}
                    >
                      Calculate
                    </Button>
                  )}
                </Box>
              </Stack>

              <Alert severity="info" sx={{ mt: 2 }}>
                <span dangerouslySetInnerHTML={{ __html: t('packages:dialogs.edit.autoCalculationAlert') }} />
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditDialog} disabled={editLoading}>
            {t('packages:dialogs.edit.cancel')}
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading || !editWeight || !editCbm}
            startIcon={editLoading ? <CircularProgress size={20} /> : <Edit />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B01030' } }}
          >
            {editLoading ? t('packages:dialogs.edit.saving') : t('packages:dialogs.edit.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Package Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('packages:dialogs.details.title')}</Typography>
          <IconButton onClick={handleCloseDetailsDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPackage && (
            <Box>
              {canSplitPackageFromAdminView(selectedPackage) && (
                <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" fontWeight="600">
                      {t('packages:dialogs.details.splitSectionTitle', { defaultValue: 'Split package items' })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('packages:dialogs.split.selectOrders')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('packages:dialogs.details.splitSelectionHint', { defaultValue: 'Only active orders can be selected for splitting.' })}
                    </Typography>
                    {selectedPackage.package_type === 'mixed' && (
                      <Alert severity="info" sx={{ alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body2">
                            <strong>{t('packages:dialogs.split.quickAction')}</strong> {t('packages:dialogs.split.quickActionDescription')}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AutoFixHigh />}
                            onClick={handleAutoSplit}
                            disabled={splitLoading}
                          >
                            {t('packages:dialogs.split.autoSplit')}
                          </Button>
                        </Box>
                      </Alert>
                    )}
                    {getSplittableOrders(selectedPackage).length === 0 && (
                      <Alert severity="warning">
                        {t('packages:dialogs.split.noActiveOrders')}
                      </Alert>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Package Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>{selectedPackage.package_number}</Typography>
                <Chip
                  label={getStatusLabel(selectedPackage.packaging_status)}
                  {...getStatusChipProps(selectedPackage.packaging_status, { mb: 2 })}
                />
                
                <Typography variant="body2" color="text.secondary">
                  {t('packages:dialogs.details.customer')} {selectedPackage.user_info?.username} ({selectedPackage.user_info?.email})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('packages:dialogs.details.created')} {new Date(selectedPackage.created_at).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('packages:dialogs.details.shippingOption')} {getShippingOptionLabel(selectedPackage.shipping_option)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pickup Mode: {getPickupOptionLabel(selectedPackage.pickup_option)}
                </Typography>
                {selectedPackage.shipping_method && (
                  <Typography variant="body2" color="text.secondary">
                    {t('packages:dialogs.details.shippingMethod')} {selectedPackage.shipping_method === 'sea' ? t('packages:shippingMethod.seaFreight') : t('packages:shippingMethod.airFreight')}
                  </Typography>
                )}
                {selectedPackage.shipping_method_selected_date && (
                  <Typography variant="body2" color="text.secondary">
                    {t('packages:dialogs.details.methodSelected')} {new Date(selectedPackage.shipping_method_selected_date).toLocaleString()}
                  </Typography>
                )}
                {selectedPackage.international_shipping_cost && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      International Shipping Fee: ₦{parseFloat(selectedPackage.international_shipping_cost).toFixed(2)}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 1 }}>
                      <Chip
                        label={
                          selectedPackage.international_shipping_fee_paid
                            ? 'International fee paid'
                            : (selectedPackage.international_shipping_fee_status === 'confirmed'
                              ? 'Final international fee unpaid'
                              : 'International fee estimate')
                        }
                        color={
                          selectedPackage.international_shipping_fee_paid
                            ? 'success'
                            : (selectedPackage.international_shipping_fee_status === 'confirmed' ? 'warning' : 'default')
                        }
                        size="small"
                      />
                      {selectedPackage.international_shipping_fee_paid_at && (
                        <Typography variant="caption" color="text.secondary">
                          Paid: {new Date(selectedPackage.international_shipping_fee_paid_at).toLocaleString()}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>

              {selectedPackage.is_fulfillment_package ? (
                <>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    External Requests ({selectedPackage.fulfillment_requests?.length || 0})
                  </Typography>
                  {(selectedPackage.fulfillment_requests || []).map((request) => (
                    <Box
                      key={request.sid || request.request_number}
                      sx={{
                        p: 2,
                        mb: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap', rowGap: 1 }}>
                        <Typography variant="body2" fontWeight="600" sx={{ fontFamily: 'monospace' }}>
                          {request.request_number}
                        </Typography>
                        <Chip label={request.status_display || request.status} size="small" />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Tracking: {request.tracking_number || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Service: {request.service_type || 'Receive & Package'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Items: {request.items_count || 0} | Source packages: {request.package_count || 1}
                      </Typography>
                      {(request.seller_name || request.seller_order_number) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Seller: {[request.seller_name, request.seller_order_number].filter(Boolean).join(' - ')}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </>
              ) : (
                <>
                  {/* Orders List */}
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    {t('packages:dialogs.details.ordersInPackage', { count: selectedPackage.total_orders })}
                  </Typography>
                  {selectedPackage.orders && selectedPackage.orders.map((order) => {
                const { label: orderStatusLabel, color: orderStatusColor } = getOrderStatusMeta(order.status);
                const isSplittableOrder = SPLITTABLE_ORDER_STATUSES.includes(order.status);
                return (
                  <Box
                    key={order.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    {canSplitPackageFromAdminView(selectedPackage) && (
                      <Checkbox
                        checked={selectedOrderIds.includes(order.id)}
                        onChange={() => handleOrderSelection(order.id)}
                        disabled={!isSplittableOrder || splitLoading}
                        inputProps={{ 'aria-label': `Select order ${order.order_number} for split` }}
                      />
                    )}
                    {order.pic_url && (
                      <Avatar src={order.pic_url} variant="rounded" sx={{ width: 50, height: 50 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="500">{order.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.order_number} • ¥{order.price} × {order.total_quantity}
                      </Typography>
                      {canSplitPackageFromAdminView(selectedPackage) && !isSplittableOrder && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('packages:dialogs.details.orderNotEligibleForSplit', { defaultValue: 'This order cannot be selected for split in its current status.' })}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={orderStatusLabel}
                      color={orderStatusColor}
                      size="small"
                    />
                  </Box>
                );
                  })}
                </>
              )}

              {!selectedPackage.is_fulfillment_package && canSplitPackageFromAdminView(selectedPackage) && getSplittableOrders(selectedPackage).length > 0 && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2, mb: 3 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {t('packages:dialogs.details.selectedCount', {
                      count: selectedOrderIds.length,
                      defaultValue: `${selectedOrderIds.length} selected`,
                    })}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedOrderIds([])}
                    disabled={splitLoading || selectedOrderIds.length === 0}
                  >
                    {t('packages:dialogs.details.clearSelection', { defaultValue: 'Clear Selection' })}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => submitPackageSplit({ packageData: selectedPackage, orderIds: selectedOrderIds })}
                    disabled={splitLoading || selectedOrderIds.length === 0}
                    startIcon={splitLoading ? <CircularProgress size={20} color="inherit" /> : <CallSplit />}
                  >
                    {t('packages:dialogs.split.splitPackage')}
                  </Button>
                </Stack>
              )}

              {/* Package Info */}
              {(selectedPackage.package_weight || selectedPackage.package_cbm || selectedPackage.package_dimension) && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    {t('packages:dialogs.details.packageInformation')}
                  </Typography>
                  {selectedPackage.package_weight && (
                    <Typography variant="body2">{t('packages:dialogs.details.weight')} {selectedPackage.package_weight} {t('packages:dialogs.details.kg')}</Typography>
                  )}
                  {selectedPackage.package_dimension && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{t('packages:dialogs.details.dimension')}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {normalizePackageDimensionText(selectedPackage.package_dimension) || selectedPackage.package_dimension}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total: {calculateCBM(selectedPackage.package_dimension) || selectedPackage.package_cbm || '—'} {t('packages:dialogs.details.m3')}
                      </Typography>
                    </Box>
                  )}
                  {!selectedPackage.package_dimension && selectedPackage.package_cbm && (
                    <Typography variant="body2">{t('packages:dialogs.details.cbm')} {selectedPackage.package_cbm} {t('packages:dialogs.details.m3')}</Typography>
                  )}
                  {selectedPackage.carton !== null && selectedPackage.carton !== undefined && (
                    <Typography variant="body2">Carton: {selectedPackage.carton}</Typography>
                  )}
                </Box>
              )}

              {selectedPackage.latest_transfer_payment_approval && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Transfer Approval Log
                  </Typography>
                  <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'grey.50' }}>
                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        Approved by: {selectedPackage.latest_transfer_payment_approval.approved_by_display || '—'}
                      </Typography>
                      <Typography variant="body2">
                        Approved at: {selectedPackage.latest_transfer_payment_approval.approved_at ? new Date(selectedPackage.latest_transfer_payment_approval.approved_at).toLocaleString() : '—'}
                      </Typography>
                      <Typography variant="body2">
                        Admin email: {selectedPackage.latest_transfer_payment_approval.approved_by_email || '—'}
                      </Typography>
                      <Typography variant="body2">
                        Role: {selectedPackage.latest_transfer_payment_approval.approved_by_role || '—'}
                      </Typography>
                      <Typography variant="body2">
                        IP address: {selectedPackage.latest_transfer_payment_approval.request_ip || selectedPackage.latest_transfer_payment_approval.remote_addr || '—'}
                      </Typography>
                      <Typography variant="body2">
                        Forwarded for: {selectedPackage.latest_transfer_payment_approval.forwarded_for || '—'}
                      </Typography>
                      <Typography variant="body2">
                        Browser: {selectedPackage.latest_transfer_payment_approval.browser || '—'}
                      </Typography>
                      <Typography variant="body2">
                        Platform / OS: {[selectedPackage.latest_transfer_payment_approval.platform, selectedPackage.latest_transfer_payment_approval.os_name].filter(Boolean).join(' / ') || '—'}
                      </Typography>
                      <Typography variant="body2">
                        Device: {selectedPackage.latest_transfer_payment_approval.device_name || '—'}
                      </Typography>
                      <Typography variant="body2">
                        Total paid: {formatNaira(selectedPackage.latest_transfer_payment_approval.total_amount)}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        Note: {selectedPackage.latest_transfer_payment_approval.note || '—'}
                      </Typography>
                      {selectedPackage.latest_transfer_payment_approval.receipt_url && (
                        <Button
                          component="a"
                          href={selectedPackage.latest_transfer_payment_approval.receipt_url}
                          target="_blank"
                          rel="noreferrer"
                          variant="outlined"
                          size="small"
                          sx={{ alignSelf: 'flex-start', mt: 1 }}
                        >
                          View Receipt
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </Box>
              )}

              {selectedPackage.shipping_option === 'self' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    {t('packages:dialogs.details.shippingAgentInformation')}
                  </Typography>
                  <Typography variant="body2">
                    {t('packages:dialogs.details.agentName')} {selectedPackage.custom_shipping_agent_name || '—'}
                  </Typography>
                  <Typography variant="body2">
                    {t('packages:dialogs.details.contactPerson')} {selectedPackage.custom_shipping_contact_person || '—'}
                  </Typography>
                  <Typography variant="body2">
                    {t('packages:dialogs.details.contactPhone')} {selectedPackage.custom_shipping_contact_phone || '—'}
                  </Typography>
                  <Typography variant="body2">
                    {t('packages:dialogs.details.city')} {selectedPackage.custom_shipping_agent_city || '—'}
                  </Typography>
                  <Typography variant="body2">
                    {t('packages:dialogs.details.province')} {selectedPackage.custom_shipping_agent_province || '—'}
                  </Typography>
                  <Typography variant="body2">
                    {t('packages:dialogs.details.address')} {selectedPackage.custom_shipping_agent_address || '—'}
                  </Typography>
                </Box>
              )}

              {/* Package Photos */}
              {selectedPackage.package_photos && selectedPackage.package_photos.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    {t('packages:dialogs.details.packagePhotos')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedPackage.package_photos.map((photo, index) => (
                      <Box
                        key={`${photo}-${index}`}
                        sx={{
                          width: 112,
                          height: 112,
                          position: 'relative',
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        <Box
                          component="img"
                          src={photo}
                          alt={`Package ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        <Tooltip title="Delete package proof image">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!!deletingPackagePhoto[`${selectedPackage.id}-${index}`]}
                              onClick={() => handleDeletePackagePhoto(photo, index)}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,1)',
                                },
                              }}
                            >
                              {deletingPackagePhoto[`${selectedPackage.id}-${index}`] ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <DeleteForever fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Material Selection Section */}
              <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Packaging Material Selection
                </Typography>
                
                {selectedPackage.material_selection ? (
                  <Box>
                    {selectedPackage.material_selection.estimated_size_by_worker && (
                      <Typography variant="body2" color="text.secondary">
                        Worker Estimate: {selectedPackage.material_selection.estimated_material_type_by_worker === 'carton' ? 'Carton' : 'Sack Bag'} - {selectedPackage.material_selection.estimated_size_by_worker}
                      </Typography>
                    )}
                    {selectedPackage.material_selection.suggested_material && (
                      <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                        Suggested: {selectedPackage.material_selection.suggested_material.name} (₦{parseFloat(selectedPackage.material_selection.suggested_material.price_per_unit).toFixed(2)})
                      </Alert>
                    )}
                    {selectedPackage.material_selection.customer_selected_material && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          Customer Selected: {selectedPackage.material_selection.customer_selected_material.name} (₦{parseFloat(selectedPackage.material_selection.customer_selected_material.price_per_unit).toFixed(2)})
                        </Typography>
                        {selectedPackage.material_selection.customer_selected_at && (
                          <Typography variant="caption" color="text.secondary">
                            Selected on: {new Date(selectedPackage.material_selection.customer_selected_at).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {selectedPackage.material_selection.actual_material_used && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          Actual Material Used: {selectedPackage.material_selection.actual_material_used.name} (₦{parseFloat(selectedPackage.material_selection.actual_material_used.price_per_unit).toFixed(2)})
                        </Typography>
                        {selectedPackage.material_selection.cost_difference > 0 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            Additional Cost: ₦{parseFloat(selectedPackage.material_selection.cost_difference).toFixed(2)}
                            {selectedPackage.material_selection.invoice_created && selectedPackage.material_selection.invoice && (
                              <Typography variant="caption" display="block">
                                Invoice: {selectedPackage.material_selection.invoice.invoice_number}
                              </Typography>
                            )}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No material assessment yet
                  </Typography>
                )}

                {/* Worker Assessment Form */}
                {selectedPackage.packaging_status === 'ready_to_package' && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Worker Assessment</Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Material Type</InputLabel>
                          <Select
                            value={materialAssessmentForm.estimated_material_type}
                            label="Material Type"
                            onChange={(e) => setMaterialAssessmentForm({ ...materialAssessmentForm, estimated_material_type: e.target.value })}
                          >
                            <MenuItem value="carton">Carton</MenuItem>
                            <MenuItem value="sack_bag">Sack Bag</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Estimated Size</InputLabel>
                          <Select
                            value={materialAssessmentForm.estimated_size}
                            label="Estimated Size"
                            onChange={(e) => setMaterialAssessmentForm({ ...materialAssessmentForm, estimated_size: e.target.value })}
                          >
                            <MenuItem value="xsmall">Extra Small</MenuItem>
                            <MenuItem value="small">Small</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="large">Large</MenuItem>
                            <MenuItem value="xlarge">Extra Large</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Notes (Optional)"
                          multiline
                          rows={2}
                          value={materialAssessmentForm.notes}
                          onChange={(e) => setMaterialAssessmentForm({ ...materialAssessmentForm, notes: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          onClick={handleAssessMaterial}
                          disabled={assessingMaterial || !materialAssessmentForm.estimated_size || !materialAssessmentForm.estimated_material_type}
                          startIcon={assessingMaterial ? <CircularProgress size={20} /> : null}
                        >
                          {assessingMaterial ? 'Assessing...' : 'Assess Material'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Admin Override Form */}
                {(selectedPackage.packaging_status === 'material_selected' || selectedPackage.packaging_status === 'packaging') && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Set Actual Material Used</Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Actual Material</InputLabel>
                          <Select
                            value={actualMaterialForm.material_id}
                            label="Actual Material"
                            onChange={(e) => setActualMaterialForm({ ...actualMaterialForm, material_id: e.target.value })}
                          >
                            {availableMaterials.map((material) => (
                              <MenuItem key={material.id} value={material.id}>
                                {material.name} - ₦{parseFloat(material.price_per_unit).toFixed(2)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Notes (Optional)"
                          multiline
                          rows={2}
                          value={actualMaterialForm.notes}
                          onChange={(e) => setActualMaterialForm({ ...actualMaterialForm, notes: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={handleSetActualMaterial}
                          disabled={settingActualMaterial || !actualMaterialForm.material_id}
                          startIcon={settingActualMaterial ? <CircularProgress size={20} /> : null}
                        >
                          {settingActualMaterial ? 'Setting...' : 'Set Actual Material'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>{t('packages:dialogs.details.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transferPaymentDialogOpen} onClose={handleCloseTransferPaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" color="success.main">
            Pay with Transfer
          </Typography>
          <IconButton onClick={handleCloseTransferPaymentDialog} size="small" disabled={transferPaymentSubmitting}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {transferPaymentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Alert severity={transferPaymentPreview?.can_process ? 'info' : (transferPaymentSavedApproval ? 'success' : 'warning')}>
                <strong>Package:</strong> {selectedPackage?.package_number || '—'}<br />
                <strong>Customer:</strong> {selectedPackage?.user_info?.username || '—'}
              </Alert>

              {transferPaymentSavedApproval && (
                <Alert severity="success">
                  A transfer approval has already been recorded for this package. You can review the saved receipt, note, and audit details below.
                </Alert>
              )}

              {transferPaymentPreview && (
                <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {transferPaymentPreview.can_process ? 'Amount due' : 'Outstanding amount'}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {formatNaira(transferPaymentPreview.total_amount)}
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                    <Typography variant="body2">
                      International shipping: {formatNaira(transferPaymentPreview.international_amount)}
                    </Typography>
                    <Typography variant="body2">
                      Local logistics: {formatNaira(transferPaymentPreview.local_amount)}
                    </Typography>
                    {!!Number.parseFloat(transferPaymentPreview.local_fee_rmb || 0) && (
                      <Typography variant="caption" color="text.secondary">
                        Local logistics source fee: ¥{Number.parseFloat(transferPaymentPreview.local_fee_rmb).toFixed(2)}
                      </Typography>
                    )}
                    {transferPaymentPreview.local_fee_included_in_international && !!Number.parseFloat(transferPaymentPreview.included_local_logistics_fee_ngn || 0) && (
                      <Typography variant="caption" color="text.secondary">
                        Local logistics is already included in the international total.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {transferPaymentPreview?.blocking_reasons?.length > 0 && !transferPaymentPreview?.can_process && (
                <Alert severity="warning">
                  {transferPaymentPreview.blocking_reasons[0]}
                </Alert>
              )}

              {transferPaymentSavedApproval && (
                <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Latest transfer approval
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">
                      Approved by: {transferPaymentSavedApproval.approved_by_display || '—'}
                    </Typography>
                    <Typography variant="body2">
                      Approved at: {transferPaymentSavedApproval.approved_at ? new Date(transferPaymentSavedApproval.approved_at).toLocaleString() : '—'}
                    </Typography>
                    <Typography variant="body2">
                      IP address: {transferPaymentSavedApproval.request_ip || transferPaymentSavedApproval.remote_addr || '—'}
                    </Typography>
                    <Typography variant="body2">
                      Device: {[
                        transferPaymentSavedApproval.browser,
                        transferPaymentSavedApproval.platform || transferPaymentSavedApproval.os_name,
                        transferPaymentSavedApproval.device_name,
                      ].filter(Boolean).join(' / ') || '—'}
                    </Typography>
                    <Typography variant="body2">
                      Total recorded: {formatNaira(transferPaymentSavedApproval.total_amount)}
                    </Typography>
                    {transferPaymentSavedApproval.receipt_url && (
                      <Button
                        component="a"
                        href={transferPaymentSavedApproval.receipt_url}
                        target="_blank"
                        rel="noreferrer"
                        variant="text"
                        sx={{ alignSelf: 'flex-start', px: 0 }}
                      >
                        View saved receipt: {getTransferReceiptName(transferPaymentSavedApproval) || 'Open receipt'}
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}

              <Box>
                {transferPaymentPreview?.can_process ? (
                  <>
                    <Button component="label" variant="outlined" startIcon={<AttachFile />}>
                      Upload receipt
                      <input
                        hidden
                        type="file"
                        onChange={(e) => setTransferPaymentReceipt(e.target.files?.[0] || null)}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Supports PDF, JPG, JPEG, PNG, and other receipt file types.
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Receipt upload is disabled because there is no outstanding transfer payment to record for this package.
                  </Typography>
                )}
                {transferPaymentReceipt && (
                  <Chip
                    label={transferPaymentReceipt.name}
                    onDelete={transferPaymentSubmitting ? undefined : () => setTransferPaymentReceipt(null)}
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <TextField
                label="Admin note (optional)"
                fullWidth
                multiline
                minRows={3}
                value={transferPaymentNote}
                onChange={(e) => setTransferPaymentNote(e.target.value)}
                InputProps={{ readOnly: !transferPaymentPreview?.can_process }}
              />

              <FormControlLabel
                control={(
                  <Checkbox
                    checked={transferPaymentConfirmed}
                    onChange={(e) => setTransferPaymentConfirmed(e.target.checked)}
                    disabled={!transferPaymentPreview?.can_process}
                  />
                )}
                label="I confirm that the customer transfer has been received and the uploaded receipt matches this package payment."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransferPaymentDialog} disabled={transferPaymentSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleTransferPaymentSubmit}
            disabled={
              transferPaymentLoading ||
              transferPaymentSubmitting ||
              !transferPaymentPreview?.can_process ||
              !transferPaymentReceipt ||
              !transferPaymentConfirmed
            }
            startIcon={transferPaymentSubmitting ? <CircularProgress size={20} color="inherit" /> : <AccountBalance />}
          >
            Confirm Transfer Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cost Distribution Dialog */}
      <Dialog open={distributeDialogOpen} onClose={() => setDistributeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Distribute Shipping Costs</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the TOTAL shipping cost (International Shipping + Local Logistics Fee).
            The system will calculate the unit cost and distribute it among all orders in this package based on their quantity.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Package:</strong> {selectedPackage?.package_number}<br/>
            <strong>Total Orders:</strong> {selectedPackage?.orders?.length || 0}<br/>
            <strong>Total Items:</strong> {selectedPackage?.orders?.reduce((sum, order) => sum + (order.total_quantity || 0), 0) || 0}
          </Alert>

          <TextField
            autoFocus
            label="Total Shipping Cost (₦)"
            type="number"
            fullWidth
            variant="outlined"
            value={distributeTotalCost}
            onChange={(e) => setDistributeTotalCost(e.target.value)}
            placeholder="e.g. 20000"
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>₦</Typography>,
            }}
          />

          <TextField
            label="Payment Due Date (Optional)"
            type="datetime-local"
            fullWidth
            variant="outlined"
            value={distributeDueDate}
            onChange={(e) => setDistributeDueDate(e.target.value)}
            sx={{ mt: 3 }}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="If set, unpaid users will be auto-detached after this date"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDistributeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDistributeSubmit} 
            variant="contained" 
            color="primary"
            disabled={distributeLoading}
            startIcon={distributeLoading ? <CircularProgress size={20} color="inherit" /> : <Calculate />}
          >
            Distribute Costs
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split Package Dialog */}
      <Dialog open={splitDialogOpen} onClose={() => setSplitDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('packages:dialogs.split.title', { packageNumber: selectedPackage?.package_number })}
          {selectedPackage?.package_type === 'mixed' && (
            <Chip label={t('packages:goodsType.mixed')} color="warning" size="small" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedPackage?.package_type === 'mixed' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">
                  <strong>{t('packages:dialogs.split.quickAction')}</strong> {t('packages:dialogs.split.quickActionDescription')}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoFixHigh />}
                  onClick={handleAutoSplit}
                  disabled={splitLoading}
                >
                  {t('packages:dialogs.split.autoSplit')}
                </Button>
              </Box>
            </Alert>
          )}
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('packages:dialogs.split.selectOrders')}
          </Typography>
          
          {selectedPackage?.orders?.filter(o => [3, 4].includes(o.status)).map((order) => (
            <Card key={order.id} sx={{ mb: 1 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                <Checkbox
                  checked={selectedOrderIds.includes(order.id)}
                  onChange={() => handleOrderSelection(order.id)}
                />
                <Avatar
                  src={order.pic_url || order.picPath || ''}
                  alt={order.title || order.order_number}
                  variant="rounded"
                  sx={{ width: 56, height: 56, ml: 1 }}
                />
                <Box sx={{ flexGrow: 1, ml: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {order.order_number}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.title}
                  </Typography>
                </Box>
                <Chip
                  label={order.goods_type === 'special' ? t('packages:goodsType.special') : t('packages:goodsType.normal')}
                  color={order.goods_type === 'special' ? 'warning' : 'success'}
                  size="small"
                />
              </CardContent>
            </Card>
          ))}
          
          {selectedPackage?.orders?.filter(o => [3, 4].includes(o.status)).length === 0 && (
            <Alert severity="warning">
              {t('packages:dialogs.split.noActiveOrders')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitDialogOpen(false)}>{t('packages:dialogs.split.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSplitSubmit}
            disabled={splitLoading || selectedOrderIds.length === 0}
            startIcon={splitLoading ? <CircularProgress size={20} color="inherit" /> : <CallSplit />}
          >
            {t('packages:dialogs.split.splitPackage')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispute Conversation Dialog */}
      <Dialog open={disputeDialogOpen} onClose={() => setDisputeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FF5722', color: 'white' }}>
          <Typography variant="h6" fontWeight="bold">
            Dispute - Package {disputePackage?.package_number}
          </Typography>
          <IconButton onClick={() => setDisputeDialogOpen(false)} size="small" sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, minHeight: 400 }}>
          {disputeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : disputeData ? (
            <Box>
              {/* Dispute Status */}
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={disputeData.status === 'open' ? 'Open' : disputeData.status === 'resolved' ? 'Resolved' : 'Cancelled'}
                  color={disputeData.status === 'open' ? 'error' : disputeData.status === 'resolved' ? 'success' : 'default'}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(disputeData.created_at).toLocaleString()}
                </Typography>
              </Box>

              {/* Conversation Messages */}
              <Box sx={{ mb: 3, maxHeight: 400, overflowY: 'auto' }}>
                {/* Initial dispute message */}
                <Card sx={{ mb: 2, backgroundColor: '#FFF3E0' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#FF9800' }}>
                        {disputePackage?.user_info?.username?.charAt(0).toUpperCase() || 'U'}
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {disputePackage?.user_info?.username || 'Customer'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {new Date(disputeData.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {disputeData.description}
                    </Typography>
                    {disputeData.images && disputeData.images.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {disputeData.images.map((img, idx) => (
                          <img key={idx} src={img} alt={`Evidence ${idx + 1}`} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }} />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Conversation messages */}
                {disputeData.messages && disputeData.messages.map((msg) => (
                  <Card key={msg.id} sx={{ mb: 2, backgroundColor: msg.message_type === 'admin' ? '#E3F2FD' : '#FFF3E0', ml: msg.message_type === 'admin' ? 4 : 0 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: msg.message_type === 'admin' ? '#2196F3' : '#FF9800' }}>
                          {msg.sender_username?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {msg.sender_username || (msg.message_type === 'admin' ? 'Admin' : 'Customer')}
                        </Typography>
                        <Chip label={msg.message_type === 'admin' ? 'Admin' : 'Customer'} size="small" sx={{ ml: 1 }} color={msg.message_type === 'admin' ? 'primary' : 'warning'} />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {new Date(msg.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {msg.message}
                      </Typography>
                      {msg.images && msg.images.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {msg.images.map((img, idx) => (
                            <img key={idx} src={img} alt={`Attachment ${idx + 1}`} style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }} />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {/* Reply Section (only if dispute is open) */}
              {disputeData.status === 'open' && (
                <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Reply to Customer
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={disputeReply}
                    onChange={(e) => setDisputeReply(e.target.value)}
                    placeholder="Type your reply..."
                    sx={{ mb: 2 }}
                  />
                  
                  {/* Image upload */}
                  <Box sx={{ mb: 2 }}>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        Array.from(e.target.files).forEach(file => handleDisputeImageUpload(file));
                      }}
                      style={{ display: 'none' }}
                      id="dispute-image-upload"
                    />
                    <label htmlFor="dispute-image-upload">
                      <Button
                        component="span"
                        size="small"
                        startIcon={<AttachFile />}
                        variant="outlined"
                      >
                        Attach Images
                      </Button>
                    </label>
                    {disputeReplyImages.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {disputeReplyImages.map((img, idx) => (
                          <Box key={idx} sx={{ position: 'relative' }}>
                            <img src={img} alt={`Preview ${idx + 1}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                            <IconButton
                              size="small"
                              onClick={() => setDisputeReplyImages(prev => prev.filter((_, i) => i !== idx))}
                              sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={disputeReplyLoading ? <CircularProgress size={20} /> : <Send />}
                    onClick={handleDisputeReplySubmit}
                    disabled={disputeReplyLoading || !disputeReply.trim()}
                    sx={{ backgroundColor: '#FF5722', '&:hover': { backgroundColor: '#E64A19' } }}
                  >
                    {disputeReplyLoading ? 'Sending...' : 'Send Reply'}
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="error">No dispute data available</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setDisputeDialogOpen(false)}>Close</Button>
          {disputeData && disputeData.status === 'open' && (
            <Button
              variant="contained"
              color="success"
              startIcon={disputeResolveLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              onClick={handleResolveDispute}
              disabled={disputeResolveLoading}
              sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#45a049' } }}
            >
              {disputeResolveLoading ? 'Resolving...' : 'Resolve Dispute'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Pool Details Dialog */}
      <Dialog
        open={poolDetailsDialogOpen}
        onClose={() => setPoolDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Pool Details: {poolDetails?.pool_id}
        </DialogTitle>
        <DialogContent>
          {poolDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : poolDetails ? (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip label={poolDetails.status} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Shipping Method</Typography>
                  <Typography>{poolDetails.shipping_method === 'air' ? 'Air' : 'Sea'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Destination</Typography>
                  <Typography>{poolDetails.destination_country}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Participants</Typography>
                  <Typography>{poolDetails.current_participants} / {poolDetails.max_participants}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Weight</Typography>
                  <Typography>{poolDetails.current_weight_kg} kg</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Volume</Typography>
                  <Typography>{poolDetails.current_volume_cbm} CBM</Typography>
                </Grid>
                {poolDetails.total_shipping_cost && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Total Shipping Cost</Typography>
                    <Typography>₦{parseFloat(poolDetails.total_shipping_cost).toFixed(2)}</Typography>
                  </Grid>
                )}
                {poolDetails.payment_deadline && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Payment Deadline</Typography>
                    <Typography>{new Date(poolDetails.payment_deadline).toLocaleString()}</Typography>
                  </Grid>
                )}
                {poolDetails.participants && poolDetails.participants.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Participants
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {poolDetails.participants.map((participant) => (
                        <Card key={participant.id} sx={{ mb: 1, p: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {participant.user_username || participant.user_email}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {participant.package_ids?.length || 0} packages • 
                                {participant.weight_kg} kg • {participant.volume_cbm} CBM
                              </Typography>
                              {participant.pooled_shipping_cost && (
                                <Typography variant="body2" color="primary" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                  Amount: ₦{parseFloat(participant.pooled_shipping_cost).toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ ml: 2 }}>
                              <Chip
                                label={participant.payment_status || 'pending'}
                                size="small"
                                color={participant.payment_status === 'paid' ? 'success' : 'warning'}
                              />
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Alert severity="error">Failed to load pool details</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoolDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PackagesPage;
